import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { initClickTracking } from "@/lib/pixel";
import "@/App.css";

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Location = lazy(() => import("@/pages/LocationPage"));
const Amenities = lazy(() => import("@/pages/Amenities"));
const FloorPlans = lazy(() => import("@/pages/FloorPlans"));
const MasterPlan = lazy(() => import("@/pages/MasterPlan"));
const Specifications = lazy(() => import("@/pages/Specifications"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Price = lazy(() => import("@/pages/Price"));
const ConstructionUpdates = lazy(() => import("@/pages/ConstructionUpdates"));
const Developer = lazy(() => import("@/pages/Developer"));
const Blogs = lazy(() => import("@/pages/Blogs"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));
const HomeLoan = lazy(() => import("@/pages/HomeLoan"));
const EMICalculator = lazy(() => import("@/pages/EMICalculator"));
const StampDuty = lazy(() => import("@/pages/StampDuty"));
const CostSheet = lazy(() => import("@/pages/CostSheet"));
const FAQs = lazy(() => import("@/pages/FAQs"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const SitemapPage = lazy(() => import("@/pages/SitemapPage"));

// NRI Hub
const NRIHome = lazy(() => import("@/pages/nri/NRIHome"));
const NRIPage = lazy(() => import("@/pages/nri/NRIPage"));

// Content silos
const SiloPage = lazy(() => import("@/pages/SiloPage"));

// Admin
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

function Fallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--pcv-bg)]">
      <div className="text-center">
        <div className="w-14 h-14 border border-[color:var(--pcv-gold)] mx-auto flex items-center justify-center">
          <span className="font-serif-lux text-2xl text-[color:var(--pcv-gold)]">P</span>
        </div>
        <div className="mt-4 text-xs uppercase tracking-[0.3em] text-[color:var(--pcv-text-2)] shimmer w-40 mx-auto">Purva City Kochi</div>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    initClickTracking();
  }, []);
  return (
    <BrowserRouter>
      <Layout>
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/location" element={<Location />} />
            <Route path="/amenities" element={<Amenities />} />
            <Route path="/floor-plans" element={<FloorPlans />} />
            <Route path="/master-plan" element={<MasterPlan />} />
            <Route path="/specifications" element={<Specifications />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/price" element={<Price />} />
            <Route path="/construction-updates" element={<ConstructionUpdates />} />
            <Route path="/developer" element={<Developer />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<BlogDetail />} />
            <Route path="/home-loan" element={<HomeLoan />} />
            <Route path="/emi-calculator" element={<EMICalculator />} />
            <Route path="/stamp-duty" element={<StampDuty />} />
            <Route path="/cost-sheet" element={<CostSheet />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/sitemap" element={<SitemapPage />} />

            <Route path="/nri" element={<NRIHome />} />
            <Route path="/nri/:slug" element={<NRIPage />} />

            <Route path="/apartments-in-kochi" element={<SiloPage slug="apartments-in-kochi" />} />
            <Route path="/luxury-apartments-in-kochi" element={<SiloPage slug="luxury-apartments-in-kochi" />} />
            <Route path="/flats-in-kochi" element={<SiloPage slug="flats-in-kochi" />} />
            <Route path="/flats-in-edappally" element={<SiloPage slug="flats-in-edappally" />} />
            <Route path="/apartments-near-lulu-mall" element={<SiloPage slug="apartments-near-lulu-mall" />} />
            <Route path="/premium-apartments-kochi" element={<SiloPage slug="premium-apartments-kochi" />} />
            <Route path="/new-launch-apartments-kochi" element={<SiloPage slug="new-launch-apartments-kochi" />} />
            <Route path="/puravankara-projects-kochi" element={<SiloPage slug="puravankara-projects-kochi" />} />
            <Route path="/2-bhk-apartments-kochi" element={<SiloPage slug="2-bhk-apartments-kochi" />} />
            <Route path="/3-bhk-apartments-kochi" element={<SiloPage slug="3-bhk-apartments-kochi" />} />
            <Route path="/4-bhk-apartments-kochi" element={<SiloPage slug="4-bhk-apartments-kochi" />} />
            <Route path="/luxury-homes-kochi" element={<SiloPage slug="luxury-homes-kochi" />} />
            <Route path="/property-investment-kochi" element={<SiloPage slug="property-investment-kochi" />} />
            <Route path="/best-residential-projects-kochi" element={<SiloPage slug="best-residential-projects-kochi" />} />
            <Route path="/ready-to-move-apartments-kochi" element={<SiloPage slug="ready-to-move-apartments-kochi" />} />
            <Route path="/upcoming-apartments-kochi" element={<SiloPage slug="upcoming-apartments-kochi" />} />
            <Route path="/real-estate-in-kochi" element={<SiloPage slug="real-estate-in-kochi" />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
